import { Component, OnInit } from '@angular/core';
import { DocumentTemplate, DocumentTemplateType } from '../../models/Template';
import { User } from '../../models/Auth';

import { AppService } from '../../services/app.service';
import { TemplateService } from '../../services/template.service';

import { ClassifierService } from '../../services/classifier.service';
import { ITableColumn } from '../../shared/table/table.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TemplateEditComponent } from './template-edit.component';

@Component({
  selector: 'app-template-list',
  templateUrl: './template-list.component.html',
  styleUrls: ['./templates.component.scss']
})
export class TemplateListComponent implements OnInit {
    constructor(public app: AppService,
        private service: TemplateService,
        private classifier: ClassifierService,
        private modal: NgbModal
    ) { }

    readonly columns: ITableColumn[] = [
        { property: 'TemplateCode', label: 'templates_lblCodeTitle', sorts: true },
        {
            property: 'Type', label: 'templates_lblType', sorts: true, sortFn: (a: DocumentTemplate, b: DocumentTemplate) => {
                const at: string = this.app.translate(`documentTemplateType_${a.Type}`);
                const bt: string = this.app.translate(`documentTemplateType_${b.Type}`);
                
                return at.localeCompare(bt);
            }
        },
        { property: 'UseCode', label: 'templates_lblUseCode', sorts: true },
        { property: 'DateFrom', label: 'templates_lblDateFrom', sorts: true, type: 'date' },
        { property: 'DateTo', label: 'templates_lblDateTo', sorts: true, type: 'date' },
        { width: '1px' }
    ];

    readonly typeOptions = Object.keys(DocumentTemplateType);
    readonly typeDisplayFn = (type: DocumentTemplateType) => this.app.translate(`documentTemplateType_${type}`);

    items: DocumentTemplate[] = [];
    currentUser: User;
    canEdit: boolean = false;
    canDelete: boolean = false;
    userRights: { Code: string, Text: string }[] = [];
    selectedTypes: DocumentTemplateType[] = [];
    searchValue: string = '';
    page = 1;

    private allItems: DocumentTemplate[] = [];
    private readonly searchSubj = new Subject<string>();
    

    ngOnInit() {
        this.currentUser = this.app.currentUser;

        if (this.currentUser) {
            this.canEdit = (this.currentUser.rights.indexOf('ES_MAIN.TEMPLATES.EDIT') > -1);
            this.canDelete = (this.currentUser.rights.indexOf('ES_MAIN.TEMPLATES.DELETE') > -1);
        }

        this.searchSubj.pipe(debounceTime(300), distinctUntilChanged()).subscribe(term => {
            if (!term) {
                this.searchValue = '';
            } else if (term.length > 2) {
                this.searchValue = term.toLowerCase();
            }

            this.filter();
        });

        this.fetch();

        this.app.addLoading(this.classifier.getUdr()).subscribe(data => {
            let userRights = data.filter(r => r.Code.startsWith("UDR")).map(t => ({ Code: t.Code, Text: t.Value }));
            this.userRights = this.userRights.concat(userRights);
        });
    }

    search(event) {
        this.searchSubj.next(event.target.value);
    }

    getFileUrl(item: DocumentTemplate, language: string) {
        return this.service.getFileUrl(item.Id, language);
    }

    add() {
        this.edit(<DocumentTemplate>{});
    }

    edit(item: DocumentTemplate) {
        const ref = this.modal.open(TemplateEditComponent, { size: 'lg' });
        ref.componentInstance.data = item;
        ref.result.then(() => {
            this.fetch();
        }, () => { });
    }

    delete(item: DocumentTemplate) {
        this.app.confirm(this.app.translate('confirmItemDelete'), result => {
            if (!result) return;

            this.app.addLoading(this.service.delete(item.Id)).subscribe(() => {
                this.fetch();
            });
        });
    }

    filter() {
        this.items = this.getFiltered();
        this.page = 1;
    }

    private fetch() {
        this.app.addLoading(this.service.getAll()).subscribe(data => {
            this.allItems = data;
            this.items = this.getFiltered();
        });
    }

    private getFiltered() {
        return this.allItems.filter(t => {
            if (this.selectedTypes.length) {
                if (!this.selectedTypes.includes(t.Type)) return false;
            }

            if (this.searchValue.length > 2) {
                if (!t.TemplateCode?.toLowerCase().includes(this.searchValue)
                    && !t.Title?.toLowerCase().includes(this.searchValue)
                ) {
                    return false;
                }
            }

            return true;
        });
    }
}
